/**
 * 敏感文件 URL 签名（2026-10-23 合规整改 v2）
 *
 * 背景：原来是 `/uploads/x.jpg?token=<7天登录token>`
 *   → URL 会进 Nginx 日志/浏览器历史/转发链接
 *   → 等于泄露 7 天完整账号权限（可调所有接口，不止看这张图）
 *
 * 现在：改成「文件级 5 分钟短期签名」`/uploads/x.jpg?_e=1730&_s=abc`
 *   → 泄露了也只能 5 分钟内看这一张图
 *
 * ⚠️ v2 修正（2026-10-23 16:12，邢先生报「审核页材料不显示」）：
 *   v1 的 fileUrl() 在签名未就绪时返回裸 URL → 401 → <Image> 变成裂图，
 *   之后即使重渲染也不会重新请求（antd Image 缓存了错误状态）。
 *   改为：同步返回占位 + 主动 fetch 签名 → 拿到后才给真实 URL。
 *
 * 用法（和旧版一致，调用点无需改动）：
 *   <img src={fileUrl(photo)} />
 *
 * 但 v2 新增推荐用法（组件级，能正确处理加载态）：
 *   import { useSignedFileUrl } from '../utils/fileSign';
 *   const src = useSignedFileUrl(photo);
 */
import api from './api';
import React from 'react';

const _cache = new Map();      // path -> { url, expiresAt }
const _pending = new Map();    // path -> Promise
const SIG_TTL = 300;           // 和后端 DEFAULT_TTL 一致（秒）
const SAFE_MARGIN = 60;        // 提前 60 秒视为过期

const SIG_EVENT = 'file-sig-ready';

// 签名未就绪时返回的占位符（1x1 透明 GIF，避免浏览器把空 src 当当前页请求）
const PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function _isSensitive(url) {
  return url && (url.startsWith('/uploads/') || url.startsWith('/archive/'));
}

function _isAlive(entry) {
  return entry && entry.expiresAt > Date.now() + SAFE_MARGIN * 1000;
}

function _notify() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SIG_EVENT));
  }
}

/**
 * 批量预热签名（推荐在页面 onLoad / useEffect 里调一次，减少首屏等待）
 * @param {string[]} urls
 */
export async function warmupFileUrls(urls) {
  const need = [];
  (urls || []).forEach((u) => {
    if (!_isSensitive(u)) return;
    const base = String(u).split('?')[0];
    if (!_isAlive(_cache.get(base))) need.push(base);
  });
  if (!need.length) return;
  await _fetchSigs(need);
}

async function _fetchSigs(paths) {
  const paths_ = Array.from(new Set(paths.map((p) => String(p).split('?')[0])));
  if (!paths_.length) return;
  const key = paths_.sort().join('|');
  if (_pending.has(key)) return _pending.get(key);

  const p = (async () => {
    try {
      const res = await api.post('/files/sign', { paths: paths_ });
      const data = res.data || res || {};
      const items = data.items || {};
      const now = Date.now();
      Object.keys(items).forEach((path) => {
        const url = items[path];
        if (!url) return;
        let expiresAt = now + SIG_TTL * 1000;
        const m = String(url).match(/[?&]_e=(\d+)/);
        if (m) expiresAt = Number(m[1]) * 1000;
        _cache.set(path, { url, expiresAt });
      });
      _notify();
    } catch (e) {
      console.warn('[fileUrl] 获取文件签名失败', e?.message || e);
    } finally {
      _pending.delete(key);
    }
  })();

  _pending.set(key, p);
  return p;
}

export { SIG_EVENT, PLACEHOLDER };

/**
 * 把站内敏感文件 URL 转成带短期签名的 URL。
 *
 * v2：签名未就绪时返回 1x1 透明占位图（而不是会 401 的裸 URL），
 *     同时后台取签名，取到后派发事件触发重渲染。
 */
export function fileUrl(url) {
  if (!url) return '';

  const s = String(url);

  // blob:/data:/外链 → 不动
  if (
    s.startsWith('blob:') ||
    s.startsWith('data:') ||
    s.startsWith('http://') ||
    s.startsWith('https://')
  ) {
    return s;
  }

  if (!_isSensitive(s)) return s;

  // 已带签名 → 直接用
  if (s.includes('_s=') && s.includes('_e=')) return s;

  const base = s.split('?')[0];

  // 1. 命中有效缓存 → 返回签名版
  const cached = _cache.get(base);
  if (_isAlive(cached)) {
    // 🚨 2026-10-26（邢先生报「附件框内图片旋转后不保存」）
    //   病根：这里原来直接 return cached.url，把调用方传的查询参数（如旋转缓存突破 `_r=时间戳`）
    //   整个丢掉了 → 浏览器长缓存命中旧图 → 看着就像“旋转没保存”。
    //   修法：把调用方自带的查询参数（除 _e/_s 签名参数外）拼回去。
    const qi = s.indexOf('?');
    if (qi === -1) return cached.url;
    const extra = s
      .slice(qi + 1)
      .split('&')
      .filter((kv) => kv && !kv.startsWith('_e=') && !kv.startsWith('_s='))
      .join('&');
    if (!extra) return cached.url;
    return cached.url + (cached.url.includes('?') ? '&' : '?') + extra;
  }

  // 2. 未缓存 → 后台取签名
  //    ⚠️ 注意：这里返回原始 URL（不是占位图）。
  //    原因：本函数也被 <a href> 使用，返回 data: 占位图会让链接完全失效。
  //    图片类场景请用 <SignedImage>（它会在签名就绪前不渲染 <img>）。
  if (typeof window !== 'undefined') {
    Promise.resolve().then(() => _fetchSigs([base]));
  }
  return s;
}

/**
 * 同步判断是否已经拿到可用签名（用于避免闪占位图）
 */
export function isFileUrlReady(url) {
  if (!_isSensitive(url)) return true;
  const base = String(url).split('?')[0];
  return _isAlive(_cache.get(base));
}

/**
 * 组件订阅签名就绪事件。
 *
 * 用法：
 *   useEffect(() => {
 *     const off = onFileSigReady(() => setTick(t => t + 1));
 *     return off;
 *   }, []);
 */
export function onFileSigReady(cb) {
  if (typeof window === 'undefined') return () => {};
  const handler = () => cb();
  window.addEventListener(SIG_EVENT, handler);
  return () => window.removeEventListener(SIG_EVENT, handler);
}

/** 清空缓存（如切换账号时） */
export function clearFileSigCache() {
  _cache.clear();
}

/**
 * React Hook：把敏感文件 URL 转成签名 URL（自动处理加载态）
 *
 * v2 推荐用法（2026-10-23）：
 *   const src = useSignedFileUrl(photo);
 *   <img src={src} />              // src 为占位图时不会 401
 *   <Image src={src} />            // antd Image 同理
 *
 * 多个 URL 用 useSignedFileUrls。
 */
export function useSignedFileUrl(url) {
  const [, tick] = React.useState(0);
  React.useEffect(() => {
    if (!_isSensitive(url)) return undefined;
    const base = String(url).split('?')[0];
    if (!_isAlive(_cache.get(base))) _fetchSigs([base]);
    const off = onFileSigReady(() => tick((t) => t + 1));
    return off;
  }, [url]);
  return fileUrl(url);
}

export function useSignedFileUrls(urls) {
  const [, tick] = React.useState(0);
  const key = Array.isArray(urls) ? urls.join('|') : '';
  React.useEffect(() => {
    if (!Array.isArray(urls) || !urls.length) return undefined;
    const need = urls
      .filter((u) => _isSensitive(u))
      .map((u) => String(u).split('?')[0])
      .filter((b) => !_isAlive(_cache.get(b)));
    if (need.length) _fetchSigs(need);
    const off = onFileSigReady(() => tick((t) => t + 1));
    return off;
  }, [key]);
  return (urls || []).map((u) => fileUrl(u));
}
